import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
    Alert,
    CircularProgress,
    Chip,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
    SettingsBrightness as SystemIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { DeviceTokenResponse, DeviceTheme, PoplachConfig } from '../api/JsdhNotifikaceService/generated';
import { DEFAULT_POPLACH_CONFIG, parsePoplachConfig } from '../api/JsdhNotifikaceService/generated';
import { useToast } from './toast/useToast';
import PoplachConfigForm, { PRESET_LABELS } from './PoplachConfigForm';

const DeviceTokensSection: React.FC = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newTokenName, setNewTokenName] = useState('');
    const [newlyCreatedToken, setNewlyCreatedToken] = useState<string | null>(null);

    // Poplach config dialog
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [configTokenId, setConfigTokenId] = useState<number | null>(null);
    const [editingConfig, setEditingConfig] = useState<PoplachConfig>(DEFAULT_POPLACH_CONFIG);

    const {
        data: tokens,
        isLoading,
    } = useQuery<DeviceTokenResponse[]>({
        queryKey: ['deviceTokens', 'raspberry'],
        queryFn: () => JsdhNotifikaceService.getDeviceTokens('raspberry'),
    });

    const createMutation = useMutation({
        // tokenType posíláme explicitně — tato sekce spravuje VÝHRADNĚ kiosky (RPi displeje)
        // a BE default už na 'raspberry' spoléhat nesmí (mobil si tím omylem zakládal kiosek).
        mutationFn: (deviceName?: string) =>
            JsdhNotifikaceService.createDeviceToken({
                ...(deviceName ? { deviceName } : {}),
                tokenType: 'raspberry',
            }),
        onSuccess: (data) => {
            setNewlyCreatedToken(data.token);
            setNewTokenName('');
            queryClient.invalidateQueries({ queryKey: ['deviceTokens', 'raspberry'] });
            showToast('Device token byl vytvořen', 'success');
        },
        onError: () => {
            showToast('Chyba při vytváření tokenu', 'error');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (tokenId: number) => JsdhNotifikaceService.deleteDeviceToken(tokenId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['deviceTokens', 'raspberry'] });
            showToast('Device token byl smazán', 'success');
        },
        onError: () => {
            showToast('Chyba při mazání tokenu', 'error');
        },
    });

    const updateThemeMutation = useMutation({
        mutationFn: ({ tokenId, theme }: { tokenId: number; theme: DeviceTheme }) =>
            JsdhNotifikaceService.updateDeviceToken(tokenId, { theme }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['deviceTokens', 'raspberry'] });
            showToast('Theme bylo aktualizováno. Stránka na RPi se aktualizuje do 30 sekund.', 'success');
        },
        onError: () => {
            showToast('Chyba při aktualizaci theme', 'error');
        },
    });

    const updateConfigMutation = useMutation({
        mutationFn: ({ tokenId, poplachConfig }: { tokenId: number; poplachConfig: string }) =>
            JsdhNotifikaceService.updateDeviceToken(tokenId, { poplachConfig }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['deviceTokens', 'raspberry'] });
            showToast('Konfigurace obrazovky byla aktualizována. Stránka na RPi se aktualizuje do 30 sekund.', 'success');
            setConfigDialogOpen(false);
        },
        onError: () => {
            showToast('Chyba při aktualizaci konfigurace', 'error');
        },
    });

    const handleThemeChange = (tokenId: number, theme: DeviceTheme) => {
        updateThemeMutation.mutate({ tokenId, theme });
    };

    const getThemeIcon = (theme?: DeviceTheme) => {
        switch (theme) {
            case 'dark':
                return <DarkModeIcon fontSize="small" />;
            case 'light':
                return <LightModeIcon fontSize="small" />;
            default:
                return <SystemIcon fontSize="small" />;
        }
    };

    const handleCreate = () => {
        createMutation.mutate(newTokenName || undefined);
    };

    const handleCloseDialog = () => {
        setCreateDialogOpen(false);
        setNewlyCreatedToken(null);
        setNewTokenName('');
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        showToast('Zkopírováno do schránky', 'success');
    };

    const copyFullUrl = (token: string) => {
        const url = `${window.location.origin}/poplach?deviceToken=${token}`;
        navigator.clipboard.writeText(url);
        showToast('URL zkopírována do schránky', 'success');
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('cs-CZ');
    };

    // Config dialog handlers
    const openConfigDialog = (token: DeviceTokenResponse) => {
        setConfigTokenId(token.id);
        setEditingConfig(parsePoplachConfig(token.poplachConfig));
        setConfigDialogOpen(true);
    };

    const handleSaveConfig = () => {
        if (configTokenId === null) return;
        updateConfigMutation.mutate({
            tokenId: configTokenId,
            poplachConfig: JSON.stringify(editingConfig),
        });
    };

    if (isLoading) {
        return (
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    return (
        <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Device tokeny pro Raspberry Pi</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Vytvořit token
                </Button>
            </Box>

            <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                Device tokeny umožňují přístup k poplachové stránce bez přihlášení.
                Použijte URL: <code>/poplach?deviceToken=VÁŠ_TOKEN</code>
            </Alert>

            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Název zařízení</TableCell>
                            <TableCell>Token</TableCell>
                            <TableCell>Theme</TableCell>
                            <TableCell>Obrazovka</TableCell>
                            <TableCell>Vytvořeno</TableCell>
                            <TableCell>Poslední použití</TableCell>
                            <TableCell width={150}>Akce</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tokens?.map((token) => {
                            const config = parsePoplachConfig(token.poplachConfig);
                            return (
                                <TableRow key={token.id}>
                                    <TableCell>
                                        {token.deviceName || (
                                            <Typography color="text.secondary" fontStyle="italic">
                                                Bez názvu
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography
                                                variant="body2"
                                                fontFamily="monospace"
                                                sx={{
                                                    maxWidth: { xs: 80, sm: 150 },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {token.token}
                                            </Typography>
                                            <Tooltip title="Kopírovat token">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => copyToClipboard(token.token)}
                                                >
                                                    <CopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                            <Select
                                                value={token.theme || 'system'}
                                                onChange={(e) => handleThemeChange(token.id, e.target.value as DeviceTheme)}
                                                disabled={updateThemeMutation.isPending}
                                                renderValue={(value) => (
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        {getThemeIcon(value as DeviceTheme)}
                                                        <span>
                                                            {value === 'dark' ? 'Tmavé' : value === 'light' ? 'Světlé' : 'Systém'}
                                                        </span>
                                                    </Box>
                                                )}
                                            >
                                                <MenuItem value="system">
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <SystemIcon fontSize="small" />
                                                        Systém
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="light">
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LightModeIcon fontSize="small" />
                                                        Světlé
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="dark">
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <DarkModeIcon fontSize="small" />
                                                        Tmavé
                                                    </Box>
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<SettingsIcon />}
                                            label={PRESET_LABELS[config.preset] || 'Standardní'}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => openConfigDialog(token)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(token.createdAt)}</TableCell>
                                    <TableCell>
                                        {token.lastUsed ? (
                                            formatDate(token.lastUsed)
                                        ) : (
                                            <Chip label="Nikdy" size="small" variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Kopírovat celou URL">
                                            <Button
                                                size="small"
                                                onClick={() => copyFullUrl(token.token)}
                                            >
                                                URL
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Smazat">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => deleteMutation.mutate(token.id)}
                                                disabled={deleteMutation.isPending}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {(!tokens || tokens.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    Žádné device tokeny. Vytvořte nový pro připojení Raspberry Pi.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Token Dialog */}
            <Dialog open={createDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {newlyCreatedToken ? 'Token vytvořen' : 'Vytvořit nový device token'}
                </DialogTitle>
                <DialogContent>
                    {newlyCreatedToken ? (
                        <Box sx={{ pt: 1 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Token je zobrazen pouze jednou! Uložte si ho.
                            </Alert>

                            <Typography variant="subtitle2" gutterBottom>
                                Token:
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: 'grey.100',
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all',
                                    mb: 2,
                                }}
                            >
                                {newlyCreatedToken}
                            </Box>

                            <Typography variant="subtitle2" gutterBottom>
                                URL pro Raspberry Pi:
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: 'grey.100',
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all',
                                    mb: 2,
                                }}
                            >
                                {`${window.location.origin}/poplach?deviceToken=${newlyCreatedToken}`}
                            </Box>

                            <Box display="flex" gap={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<CopyIcon />}
                                    onClick={() => copyToClipboard(newlyCreatedToken)}
                                >
                                    Kopírovat token
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<CopyIcon />}
                                    onClick={() => copyFullUrl(newlyCreatedToken)}
                                >
                                    Kopírovat URL
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ pt: 1 }}>
                            <TextField
                                label="Název zařízení (volitelné)"
                                fullWidth
                                value={newTokenName}
                                onChange={(e) => setNewTokenName(e.target.value)}
                                placeholder="např. Raspberry Pi - zbrojnice"
                                helperText="Pomůže identifikovat zařízení v seznamu"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        {newlyCreatedToken ? 'Zavřít' : 'Zrušit'}
                    </Button>
                    {!newlyCreatedToken && (
                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={createMutation.isPending}
                        >
                            {createMutation.isPending ? 'Vytvářím...' : 'Vytvořit'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Poplach Config Dialog */}
            <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Konfigurace poplachové obrazovky</DialogTitle>
                <DialogContent>
                    <PoplachConfigForm config={editingConfig} onChange={(updater) => setEditingConfig(updater)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfigDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveConfig}
                        disabled={updateConfigMutation.isPending}
                    >
                        {updateConfigMutation.isPending ? 'Ukládám...' : 'Uložit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default DeviceTokensSection;
